import React, { Ref, useEffect, useMemo } from 'react'
import { format } from 'date-fns';
import * as styles from './printableShipTicket.module.scss'
import Svg from '../svg/svg';
import { Image, LoadingOverlay } from '../../common'
import { View } from '../../actionsWorkflow/actionsWorkflow.type';
import useActionDetails from '../../../hooks/useActionDetails/useActionDetails';
import { Settings } from '../../../hooks/useSettings/useSettings.type';
import { LocationObject } from '../../../hooks/useActionDetails/useActionDetails.type';

const padding = '40px';

// see Helpful Style Tips: https://www.npmjs.com/package/react-to-print
// Padding is used here as resetting Margin will result in page info printout returning in margin
const getPagePadding = () => {
  return `@page { padding: ${padding} !important; }`;
};

const convertView = (view: View) => {
    switch(view) {
        case View.Borrow:
        case View.Loan:
            return 1
        case View.Field_Transfer:
            return 3
        case View.Warehouse_Transfer:
            return 4
        case View.Return:
            return 2
        default:
            return 1
    }
}

const getFromIcon = (view: View) => {
    switch(view) {
        case View.Loan:
        case View.Borrow:
        case View.Warehouse_Transfer:
            return <Svg id='print-location-warehouse' />
        case View.Return:
        case View.Field_Transfer:
            return <Svg id='print-location-pin' />
        default: 
    }
}

const getToIcon = (view: View) => {
    switch(view) {
        case View.Loan:
        case View.Borrow:
        case View.Field_Transfer:
            return <Svg id='print-location-pin' />
        case View.Return:
        case View.Warehouse_Transfer:
            return <Svg id='print-location-warehouse' />
        default: 
    }
}

const concatenateAddress = (addressItems: string[]) => addressItems.reduce((addressString, addressItem, index) => {
    if (addressItem) {
        if (index !== addressItems.length - 1) {
            return `${addressString}${addressItem}, `
        } else {
            return `${addressString}${addressItem}`
        }
    }
    return addressString
}, '')

const locationHasInformation = (address: LocationObject) => {
    for (const [_, value] of Object.entries(address)) {
        if (value) return true
    }

    return false
}

export default ({ 
    contentRef, 
    settings, 
    view, 
    workflowResponse,
    warehouseTransferData,
}: {
    contentRef: Ref<HTMLDivElement>,
    view: View,
    settings: Settings,
    // any specified at root of hook
    workflowResponse: any,
    warehouseTransferData: {
        comments: string,
        transferToLocationId: string | number,
        items: {
            toolId: number,
            toolWarehouseId: number,
            quantity: number
        }[]
    }
}) => {
    const { 
        actionDetails,
        setActionInput,
        actionDetailsIsLoading,
    } = useActionDetails()

    const requestType = useMemo(() => {
        return convertView(view)
    }, [view])

    // Determining which icons to display for From & To based on View state
    const toIcon = useMemo(() => {
        return getToIcon(view)
    }, [view])

    const fromIcon = useMemo(() => {
        return getFromIcon(view)
    }, [view])

    // Successful items have to be manually filtered from the array of cart items
    const pickSuccessfulItems = 
        (successfulItems, warehouseTransferItems) => 
            successfulItems.map(successfulItemId => 
                warehouseTransferItems.find(warehouseItem => 
                    warehouseItem.toolId === successfulItemId
                )
            )

    // Calculate total number of items as part of a group
    const getGroupedItemQuantity = (groupedItems) => groupedItems.reduce((accumulator, currentItem) => accumulator + (currentItem.quantity || 1), 0)

    useEffect(() => {
        if (requestType && workflowResponse?.SuccessfulWithLoanID) {
            if (view !== View.Warehouse_Transfer) {
                setActionInput({
                    requestType,
                    items: workflowResponse.SuccessfulWithLoanID
                })
            } else {
                const { comments, transferToLocationId, items: itemsFromWarehouseTransferData } = warehouseTransferData
                setActionInput({
                    comments,
                    transferToLocationId,
                    requestType,
                    items: pickSuccessfulItems(workflowResponse.Successful, itemsFromWarehouseTransferData)
                })
            }
        }
    }, [requestType, workflowResponse?.SuccessfulWithLoanID, warehouseTransferData])

    const renderTicketType = () => {
        switch(view) {
            case View.Loan:
                return (
                    <h1>
                        <Svg id='print-loan'/>
                        <span>
                            Loan Ticket
                        </span>
                    </h1>
                )
            case View.Borrow:
                return (
                    <h1>
                        <Svg id='print-loan' style={{transform: 'rotate(90deg)'}} />
                        <span>
                            Borrow Ticket
                        </span>
                    </h1>
                )
            case View.Field_Transfer:
            case View.Warehouse_Transfer:
                return (
                    <h1>
                        <Svg id='print-transfer-return' />
                        <span>
                            Transfer Ticket
                        </span>
                    </h1>
                )
            case View.Return:
                return (
                    <h1>
                        <Svg id='returnRequest' style={{height: '20px', width: '20px'}} />
                        <span>
                            Return Ticket
                        </span>
                    </h1>
                )
            case View.MakeUnavailable:
            case View.MakeAvailable:
            default:
                <h1>Shipping Ticket</h1>
        }
    }

    if (actionDetails && !actionDetailsIsLoading) {
        const { 
            groupedItems, 
            companyDetails, 
            actionDate, 
            to,
            comment
        } = actionDetails 

        return (
            <div ref={contentRef} className={styles.printContent}>
                <style>{getPagePadding()}</style>
                {groupedItems.map((itemGroup) => {
                    // 'x Items' displayed at start of page is calculated here
                    const pageItemsCount = getGroupedItemQuantity(itemGroup.items)
                    return (
                        <>
                            <div>
                                <header className={styles.header}>
                                    <section>
                                        {renderTicketType()}
                                    </section>
                                    <section>
                                        <h2>
                                            {companyDetails?.companyPictureURL && (
                                                <Image 
                                                    width={20}
                                                    url={companyDetails.companyPictureURL}
                                                    isCircle
                                                />
                                            )}
                                            {companyDetails.companyDisplayName}
                                        </h2>
                                    </section>
                                    <section>
                                        <Svg id='sharemytoolbox-print-logo' />
                                    </section>
                                </header>
                                <div className={styles.fromToContainer}>
                                    <article className={styles.fromToItem}>
                                        <h3>From</h3>
                                        <ul>
                                            {itemGroup.users && (itemGroup.users.map(item => (
                                                <li>
                                                    <Image 
                                                        width={15}
                                                        url={item.userPictureURL}
                                                        isCircle
                                                    />
                                                    <span>
                                                        {item.userDisplayName} 
                                                    </span>
                                                </li>
                                            )))}
                                            {locationHasInformation(itemGroup.fromLocation) && (
                                                <li className={styles.addressInfo}>
                                                    <div>
                                                        {fromIcon}
                                                    </div>
                                                    <span>
                                                        <div>{itemGroup.fromLocation.locationName}</div>
                                                        <div>{concatenateAddress([
                                                            itemGroup.fromLocation.address,
                                                            itemGroup.fromLocation.city,
                                                            itemGroup.fromLocation.state,
                                                            itemGroup.fromLocation.postalCode,
                                                        ])}</div>
                                                    </span>
                                                </li>
                                            )}
                                            <li>
                                                <Svg id='print-calendar-from' />
                                                <span>
                                                    On {format(new Date(actionDate), 'd MMM yyyy')}
                                                </span>
                                            </li>
                                        </ul>
                                    </article>
                                    <article className={styles.fromToItem}>
                                        <h3>To</h3>
                                        <ul>
                                            <li>
                                                <Image
                                                    width={15}
                                                    isCircle
                                                    url={to.userPictureURL}
                                                />
                                                <span>
                                                    {to.userDisplayName}
                                                </span>
                                            </li>
                                            {locationHasInformation(to.location) && 
                                                (<>
                                                    <li className={styles.addressInfo}>
                                                        <div>
                                                            {toIcon}
                                                        </div>
                                                        <span>
                                                            <div>{to.location.locationName}</div>
                                                            <div>{concatenateAddress([
                                                                to.location.address,
                                                                to.location.city,
                                                                to.location.state,
                                                                to.location.postalCode,
                                                            ])}</div>
                                                        </span>
                                                    </li>
                                                </>
                                            )}
                                            {to.dueBackDate && (<li>
                                                <Svg id='print-due-back-to' />
                                                <span>
                                                    Due Back {format(new Date(to?.dueBackDate), 'd MMM yyyy')}
                                                </span>
                                            </li>)}
                                        </ul>
                                    </article>
                                </div>
                                {comment && (
                                    <div className={styles.commentContainer}>
                                        <Svg id='print-comment' />
                                        <span>{comment}</span>
                                    </div>
                                )}
                                <div className={styles.itemsContainer}>
                                    <p>{pageItemsCount} {pageItemsCount > 1 ? 'items': 'item'}</p>
                                    {itemGroup.items.map((item) => {
                                        return (
                                            <>
                                                <article className={styles.item}>
                                                    <header className={styles.itemHeader}>
                                                        <h3>{item.title}</h3>
                                                        <div className={styles.tickboxContainer}>
                                                            <h4>Quantity: {item.quantity || '-'} </h4>
                                                            <div className={styles.tickBox}></div>
                                                        </div>
                                                    </header>
                                                    <p>{item.manufacturer || '-'}  | {item.modelNumber || '-'} | {item.category || '-'}</p>
                                                    <div className={styles.itemInfoGrid}>
                                                        <div className={styles.itemContainer}>
                                                            <Svg id='print-id-item' />
                                                            <p>Item ID: {item.itemId || '-'}</p>
                                                        </div>
                                                        <div className={styles.itemContainer}>
                                                            <Svg id='print-barcode-item' />
                                                            <p className={styles.barcode}>{item.barcode || '-'} </p>
                                                        </div>
                                                        <div className={styles.itemContainer}>
                                                            <Image 
                                                                url={item.fromUser?.userPictureURL}
                                                                width={10}
                                                                isCircle
                                                            />
                                                            <p>From: {item.fromUser?.userDisplayName || '-'}</p>
                                                        </div>
                                                        <div className={styles.itemContainer}>
                                                            <Svg id='print-serial-number-item' />
                                                            <p>Serial Number: {item.serialNumber || '-'}</p>
                                                        </div>
                                                        <div className={styles.itemContainer}>
                                                            <Svg id='print-custom-text-item' />
                                                            <p>{settings['CustomText1Label']}: {item.customText1Value || '-'}</p>
                                                        </div>
                                                        <div className={styles.itemContainer}>
                                                            {getFromIcon(view)}
                                                            <p>From: {itemGroup.fromLocation.locationName || '-'} </p>
                                                        </div>
                                                    </div>
                                                </article>
                                            </>
                                        )})}
                                </div>
                            </div>
                            <div className={styles.pagebreak} />
                        </>
                )})}
            </div>
        )
    }

    if (actionDetailsIsLoading) return <LoadingOverlay />
    
    return null
}
